import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import ar from './ar.json';
import en from './en.json';

export type Lang = 'en' | 'ar';
export type MessageKey = keyof typeof en;

// Compile-time check that Arabic has every English key.
const AR: Record<MessageKey, string> = ar;
const MESSAGES: Record<Lang, Record<MessageKey, string>> = { en, ar: AR };
const STORAGE_KEY = 'procure.lang';

export function translate(lang: Lang, key: MessageKey, vars: Record<string, string> = {}): string {
  return MESSAGES[lang][key].replace(/\{(\w+)\}/g, (_, name: string) => vars[name] ?? `{${name}}`);
}

export function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'ar') return saved;
  } catch {
    // Storage can be blocked (private mode, policy); English is the safe default.
  }
  return 'en';
}

export function applyLang(lang: Lang): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.title = translate(lang, 'app.titleSuffix');
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Not remembering the choice is acceptable.
  }
}

export interface I18n {
  lang: Lang;
  t: (key: MessageKey, vars?: Record<string, string>) => string;
  setLang: (lang: Lang) => void;
}

export const I18nContext = createContext<I18n>({
  lang: 'en',
  t: (key, vars) => translate('en', key, vars),
  setLang: () => undefined,
});

export const useI18n = () => useContext(I18nContext);
